import { createFileRoute, notFound, useNavigate } from '@tanstack/react-router';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { TeamPresetDashboard } from '@/components/features/teams/TeamPresetDashboard';
import { TeamPresetFormSheet } from '@/components/features/teams/TeamPresetFormSheet';
import { Button } from '@/components/ui/button';
import {
  ResponsiveDialog,
  ResponsiveDialogContent,
  ResponsiveDialogDescription,
  ResponsiveDialogFooter,
  ResponsiveDialogHeader,
  ResponsiveDialogTitle,
} from '@/components/ui/responsive-dialog';
import { deleteTeamPreset, restoreTeamPreset } from '@/db/team-presets';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';
import { usePlayers } from '@/hooks/usePlayers';
import { useTeamPreset } from '@/hooks/useTeamPreset';
import { handleUserFacingError } from '@/lib/error-handler';
import { toastDeleteWithUndo, toastError } from '@/lib/toast-helpers';
import { parseTeamId, safeParseTeamId, type TeamPreset } from '@/schemas';

export const Route = createFileRoute('/team-presets/$presetId')({
  loader: ({ params }) => {
    const parseResult = safeParseTeamId(params.presetId);
    if (!parseResult.success) {
      // eslint-disable-next-line @typescript-eslint/only-throw-error -- notFound() is TanStack Router's official API for 404 handling, not a standard Error
      throw notFound();
    }
  },
  component: TeamPresetDetailRoute,
});

function TeamPresetDetailRoute() {
  const { presetId: presetIdParam } = Route.useParams();
  const navigate = useNavigate();
  const { t } = useTranslation('common');

  const presetId = parseTeamId(presetIdParam);
  const { teamPreset, isLoading, error } = useTeamPreset(presetId);
  const { players } = usePlayers({ includeDeleted: false });

  const [showEditSheet, setShowEditSheet] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const documentTitle = useMemo(() => {
    const teamName = teamPreset?.name || t('teams.unnamedPreset', 'Unnamed Preset');
    return `${teamName} - ${t('teams.detail.title', 'Team preset')} · ${t(
      'app.name',
      'One Score',
    )}`;
  }, [teamPreset?.name, t]);

  useDocumentTitle(documentTitle);

  const handleEditClick = useCallback((_preset: TeamPreset) => {
    setShowEditSheet(true);
  }, []);

  const handleDeleteClick = useCallback((_preset: TeamPreset) => {
    setShowDeleteDialog(true);
  }, []);

  const handleDeleteConfirm = useCallback(async () => {
    if (!teamPreset) return;

    setIsDeleting(true);
    try {
      await deleteTeamPreset(teamPreset.id);
      toastDeleteWithUndo(
        t('teams.deleteSuccess', 'Team preset deleted successfully'),
        async () => {
          await restoreTeamPreset(teamPreset.id);
        },
        t('undo', 'undo'),
      );
      void navigate({ to: '/team-presets' });
    } catch (error: unknown) {
      const errorMessage = handleUserFacingError(
        error,
        t('teams.deleteError', 'Failed to delete team preset'),
        { operation: 'deleteTeamPreset', presetId: teamPreset.id },
      );
      toastError(errorMessage);
      setIsDeleting(false);
    }
  }, [teamPreset, t, navigate]);

  const handleDeleteCancel = useCallback(() => {
    setShowDeleteDialog(false);
  }, []);

  const handleEditSuccess = useCallback(() => {
    // Form sheet will close itself, we just need to refresh if needed
  }, []);

  return (
    <div className="mx-auto w-full max-w-5xl space-y-8 px-4 py-6">
      <TeamPresetDashboard
        teamPreset={teamPreset}
        players={players}
        isLoading={isLoading}
        error={error}
        onEdit={teamPreset ? handleEditClick : undefined}
        onDelete={teamPreset ? handleDeleteClick : undefined}
      />

      {/* Edit Sheet */}
      {teamPreset && (
        <TeamPresetFormSheet
          open={showEditSheet}
          onOpenChange={setShowEditSheet}
          teamPreset={teamPreset}
          onSuccess={handleEditSuccess}
        />
      )}

      {/* Delete Confirmation Dialog */}
      <ResponsiveDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <ResponsiveDialogContent className="sm:max-w-lg">
          <ResponsiveDialogHeader>
            <ResponsiveDialogTitle>
              {t('teams.deleteConfirm.title', 'Delete team preset?')}
            </ResponsiveDialogTitle>
            <ResponsiveDialogDescription>
              {t(
                'teams.deleteConfirm.description',
                "This won't affect existing sessions. You can undo this action.",
              )}
            </ResponsiveDialogDescription>
          </ResponsiveDialogHeader>
          <ResponsiveDialogFooter>
            <Button variant="outline" onClick={handleDeleteCancel} disabled={isDeleting}>
              {t('cancel')}
            </Button>
            <Button
              variant="destructive"
              onClick={() => {
                void handleDeleteConfirm();
              }}
              disabled={isDeleting}
            >
              {isDeleting
                ? t('teams.deleting', 'Deleting...')
                : t('teams.actions.delete', 'Delete')}
            </Button>
          </ResponsiveDialogFooter>
        </ResponsiveDialogContent>
      </ResponsiveDialog>
    </div>
  );
}
